import { useTranslation } from '@rhino-project/core/i18n';
import { use, useMemo } from 'react';
import { useLocation, useParams } from '@tanstack/react-router';
import {
  createChatBrowserTools,
  useBaseOwnerId,
  useFeature
} from '@rhino-project/core/hooks';
import { ChatSheet, useChatNavigation } from '@rhino-project/ui-heroui';
import TeamContext from '../../contexts/TeamContext';
import { useMode } from '../../hooks/useMode';

export const AppChat = ({ onShowResults }: { onShowResults: () => void }) => {
  const { t } = useTranslation();

  const ownerId = useBaseOwnerId();
  const enabled = useFeature('ai_chat');
  const { pathname, search: pageSearch } = useLocation();
  const params = useParams({ strict: false });
  const { team } = use(TeamContext);
  const { mode } = useMode();
  const onNavigate = useChatNavigation({
    resources: ['player', 'report'],
    onShowResults
  });
  const teamSeasonId = params.team_season_id;
  const teamId =
    String(team?.organization?.id ?? team?.organization_id) === String(ownerId)
      ? team?.id
      : null;
  const context = useMemo(
    () => ({
      page: pathname,
      team_id: teamId ?? null,
      team_season_id: teamSeasonId ?? null,
      mode
    }),
    [pathname, teamId, teamSeasonId, mode]
  );

  const browserTools = createChatBrowserTools({
    resources: ['player', 'report'],
    navigate: onNavigate,
    readContext: () => ({ ...context, filters: pageSearch })
  });

  if (!enabled) return null;
  return (
    <div className="fixed bottom-4 right-4 z-40">
      <ChatSheet
        key={ownerId}
        assistant="app"
        ownerId={ownerId}
        context={context}
        browserTools={browserTools}
        onNavigate={onNavigate}
        title={t('frontend.messages.ask_espm')}
        description={t(
          'frontend.messages.find_players_teams_rosters_and_reports_in_this_organization'
        )}
        suggestions={[
          t('frontend.messages.which_players_graduate_next_year'),
          t('frontend.messages.show_the_roster_for_this_team_and_season')
        ]}
      />
    </div>
  );
};
